import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deletePayment,
  fetchRemainingClearHistory,
} from '../../../services/receptionist/receptionistApi';
import { formatCurrency } from '../receptionistData';
import { notifyPaymentsUpdated, PAYMENTS_UPDATE_EVENT } from '../paymentStore';
import { RECEPTIONIST_BRANCH_EVENT } from '../../../services/gym/branchContext';
import { formatSerialDisplay } from '../../admin/members/memberData';
import { usePanelToast } from '../../../context/PanelToastContext';
import TableRowIconActions from '../../../components/shared/TableRowIconActions';
import RemainingClearEditModal from './RemainingClearEditModal';
import '../payments.css';
import './ancillarySales.css';

function monthStart() {
  return new Date().toISOString().slice(0, 7) + '-01';
}

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

function paymentDay(row) {
  return String(row?.date || row?.paidAt || '').slice(0, 10);
}

/**
 * Compact Remaining Clear installment history with month/date filter.
 * Shown under each sale module so reception can see clears without leaving the page.
 * Cleared rows support Edit (amount/method) and Delete.
 */
export default function RemainingClearHistory({ title = 'Remaining Clear history' }) {
  const toast = usePanelToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(todayDate);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const data = await fetchRemainingClearHistory({
        startDate: startDate || '',
        endDate: endDate || '',
      });
      setRows(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load remaining clears');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => { load(); };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    return () => {
      window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
      window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    };
  }, [load]);

  const totalCleared = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [rows],
  );

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    const paymentDbId = deleteTarget.rawId || deleteTarget.id;
    try {
      setDeleting(true);
      await deletePayment(paymentDbId);
      setDeleteTarget(null);
      notifyPaymentsUpdated();
      toast.showDeleted('Remaining clear');
      await load();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete remaining clear');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="owner-card rec-panel-card rec-member-list-card" style={{ marginTop: 20 }}>
      <div className="rec-ancillary-sale-history-header">
        <div>
          <h2 className="owner-card-title">{title}</h2>
          <p className="owner-placeholder-text" style={{ margin: '6px 0 0' }}>
            {loading ? 'Loading…' : `${rows.length} clear${rows.length === 1 ? '' : 's'}`}
            {!loading && rows.length > 0 ? ` · ${formatCurrency(totalCleared)} collected` : ''}
            {(startDate || endDate) ? ` · ${startDate || '…'} → ${endDate || '…'}` : ''}
          </p>
        </div>
      </div>

      <form
        className="panel-dash-filters"
        style={{ marginBottom: 12 }}
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="rc-hist-start">From</label>
          <input
            id="rc-hist-start"
            type="date"
            className="owner-form-input"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="rc-hist-end">To</label>
          <input
            id="rc-hist-end"
            type="date"
            className="owner-form-input"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="panel-dash-filter-actions">
          <button type="submit" className="owner-btn owner-btn-primary owner-btn-sm">Search</button>
          <button
            type="button"
            className="owner-btn owner-btn-secondary owner-btn-sm"
            onClick={() => {
              setStartDate(monthStart());
              setEndDate(todayDate());
            }}
          >
            This month
          </button>
        </div>
      </form>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}

      <div className="owner-table-wrap">
        <table className="owner-table rec-member-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Serial No</th>
              <th>Member</th>
              <th>Package / Sale</th>
              <th>Amount Cleared</th>
              <th>Method</th>
              <th>Invoice</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => (
              <tr key={row.rawId || row.id}>
                <td>{paymentDay(row) || '—'}</td>
                <td className="rec-member-gym-id">
                  {formatSerialDisplay(row.gymId || row.serialNo) || row.gymId || '—'}
                </td>
                <td>{row.member || '—'}</td>
                <td>{row.package || '—'}</td>
                <td className="finance-amount-positive">{formatCurrency(row.amount)}</td>
                <td>{row.method || '—'}</td>
                <td>{row.invoiceId || row.paymentId || '—'}</td>
                <td>{row.paymentNote || '—'}</td>
                <td>
                  <TableRowIconActions
                    onEdit={() => setEditTarget(row)}
                    onDelete={() => setDeleteTarget(row)}
                    editLabel={`Edit remaining clear for ${row.member || 'member'}`}
                    deleteLabel={`Delete remaining clear for ${row.member || 'member'}`}
                  />
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={9} className="owner-placeholder-text">
                  {loading ? 'Loading remaining clears…' : 'No remaining clears in this date range.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <RemainingClearEditModal
          key={editTarget.rawId || editTarget.id}
          row={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={load}
        />
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Remaining Clear</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete remaining clear for <strong>{deleteTarget.member || 'this member'}</strong>
              {' '}({formatCurrency(deleteTarget.amount)})?
              {' '}This restores the amount on the original sale as pending again.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
